// src/pages/MsoCalculationsPage.tsx
//
// ★★ ANNEX A 22 — the MSO calculation enquiry: every calculation the engine has recorded, answered
// or refused, newest first, with a detail view (`/mso/calculations/:id`) that renders the stored
// trace and the exact rule version it used.
//
// ⚠ **THE COLUMN HEADERS DO NOT SORT, and the reason is the route's.** `GET /api/mso-calculations`
// returns one fixed order (newest first) and accepts no sort. A header that reordered the ONE PAGE
// the browser holds would look like it had ordered the whole list while ordering a slice of it, so
// each header declares why it is static rather than offering a control that lies.
//
// ⚠ A filter change is a different population, so the pager returns to page one; the offset it
// fetches with is kept in step with the pager in one place, exactly as `ExclusionListPage` does.
import { useEffect, useId, useState } from 'react'
import { Link, useParams } from 'react-router'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { sortParams, useSort } from '../lib/useSort'
import { SortableTh, SortAnnouncement, UnsortableHint } from '../components/SortableTh'
import { useOffsetPager } from '../lib/useOffsetPager'
import { formatInstant, formatIsoDate } from '../lib/format'
import { msoRefusalLabel } from '../lib/mso-vocabulary'
import { storedText } from '../lib/redacted'
import { DatePicker } from '../components/DatePicker'
import { FieldError } from '../components/FieldError'
import { SgdsButton } from '@govtechsg/sgds-web-component/react'
import { ExportToolbar } from '../components/ExportToolbar'
import { startDownload } from '../lib/start-download'
import { Money } from '../components/Money'
import { CategoryBadge } from '../components/StatusBadge'
import { Pagination } from '../components/Pagination'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { MsoCalculationDetail } from '../components/MsoCalculationDetail'
import type { MsoCalculation } from '../lib/types'

const PAGE_SIZE = 25

/** What the stored input says about one field, or null: the input is jsonb the page did not write. */
function storedField(row: MsoCalculation, name: 'treatmentDate' | 'beneficiaryRef'): string | null {
  const input = row.input
  if (typeof input !== 'object' || input === null || Array.isArray(input)) return null
  return storedText((input as Record<string, unknown>)[name])
}

const treatmentDateText = (row: MsoCalculation): string => {
  const value = storedField(row, 'treatmentDate')
  return value !== null && /^\d{4}-\d{2}-\d{2}$/.test(value) ? formatIsoDate(value) : 'Not recorded'
}

export function MsoCalculationsPage() {
  const { id } = useParams()
  if (id !== undefined) return <MsoCalculationDetail key={id} id={id} />
  return <MsoCalculationsList />
}

type CalcSortKey = 'computed' | 'treatmentDate' | 'beneficiary' | 'outcome' | 'rule' | 'officer' | 'government' | 'refusal'
const CALC_COLUMN_LABELS: Readonly<Record<CalcSortKey, string>> = {
  computed: 'Computed',
  treatmentDate: 'Treatment date',
  beneficiary: 'Beneficiary',
  outcome: 'Outcome',
  rule: 'Rule',
  officer: 'Officer or dependant',
  government: 'Government',
  refusal: 'Refusal',
}

function MsoCalculationsList() {
  const baseId = useId()
  const { sort, onSort, interacted } = useSort<CalcSortKey>()
  const [ruleKey, setRuleKey] = useState('')
  const [outcome, setOutcome] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  // ★ F-28: a date typed in full that is not a real date ("2026-02-30"), as the text the officer typed.
  const [fromTyped, setFromTyped] = useState<string | null>(null)
  const [toTyped, setToTyped] = useState<string | null>(null)
  const [clears, setClears] = useState(0)
  const [pagerOffset, setPagerOffset] = useState(0)

  const rules = useApi(() => api.msoRules(), [])
  const calculations = useApi(
    () => api.msoCalculations({ ruleKey, outcome, from, to, limit: PAGE_SIZE, offset: pagerOffset, ...sortParams(sort) }),
    [ruleKey, outcome, from, to, pagerOffset, sort.column, sort.dir],
  )

  // N-11: a refusal (403) means this officer cannot use the screen, so none of its controls is offered beside the message
  const refused = calculations.errorStatus === 403
  const rows = calculations.data?.calculations ?? []
  const total = calculations.data?.total ?? 0
  const rangeProblem = from !== '' && to !== '' && from > to ? 'The start date is after the end date. Change one of them.' : null
  const anyFilter = ruleKey !== '' || outcome !== '' || from !== '' || to !== '' || fromTyped !== null || toTyped !== null
  const clearFilters = () => {
    setRuleKey('')
    setOutcome('')
    setFrom('')
    setTo('')
    setFromTyped(null)
    setClears((n) => n + 1)
    setToTyped(null)
  }
  const pager = useOffsetPager(PAGE_SIZE, total)

  useEffect(() => {
    if (pager.offset !== pagerOffset) setPagerOffset(pager.offset)
  }, [pager.offset, pagerOffset])
  useEffect(() => {
    pager.reset()
  }, [ruleKey, outcome, from, to, sort.column, sort.dir])

  // What is on screen is what is downloaded: the same four filters and the same order, nothing more and nothing less.
  const exportFilters = { ruleKey, outcome, from, to, ...sortParams(sort) }
  const ruleKeys = [...(rules.data?.rules ?? []).map((r) => r.ruleKey)].sort()

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl mb-2">MSO calculation enquiry</h1>
          <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
            Every MSO calculation the engine has recorded, answered or refused, newest first until you sort a column. Open one to read
            its trace and the exact rule version it used.
          </p>
        </div>
        {!refused && (
        <div className="flex flex-col items-end gap-3">
          <Link to="/mso/calculator" className="text-sm pt-link-target">
            Open the MSO calculator
          </Link>
          <ExportToolbar
            onPrint={() => window.print()}
            onDownload={(format) => {
              startDownload(api.msoCalculationsExportUrl(format, exportFilters))
            }}
          />
        </div>
        )}
      </div>

      {!refused && (
      <div className="pt-controlbar pt-no-print" role="group" aria-label="Filter the calculations">
        <div className="flex flex-col gap-1 text-sm">
          <label htmlFor={`${baseId}-rule`} className="font-semibold">
            Rule
          </label>
          <select id={`${baseId}-rule`} className="pt-native-date" value={ruleKey} onChange={(e) => setRuleKey(e.target.value)}>
            <option value="">All rules</option>
            {ruleKeys.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1 text-sm">
          <label htmlFor={`${baseId}-outcome`} className="font-semibold">
            Outcome
          </label>
          <select id={`${baseId}-outcome`} className="pt-native-date" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
            <option value="">Calculated and refused</option>
            <option value="CALCULATED">Calculated</option>
            <option value="REFUSED">Refused</option>
          </select>
        </div>
        <div className="pt-controlbar-field flex flex-col gap-1 text-sm">
          <span id={`${baseId}-from`} className="font-semibold">
            Computed from
          </span>
          <DatePicker
            key={`from-${clears}`}
            value={from}
            onChange={(v) => {
              setFrom(v)
              if (v !== '') setFromTyped(null)
            }}
            labelledBy={`${baseId}-from`}
            subject="start date"
            onTyped={(raw, valid) => setFromTyped(valid || raw.length < 10 ? null : raw)}
            {...(fromTyped !== null && { invalid: true, describedBy: `${baseId}-from-error` })}
          />
          <FieldError id={`${baseId}-from-error`} problem={fromTyped === null ? null : `${fromTyped} is not a real date. Use the form yyyy-mm-dd.`} />
        </div>
        <div className="pt-controlbar-field flex flex-col gap-1 text-sm">
          <span id={`${baseId}-to`} className="font-semibold">
            Computed to
          </span>
          <DatePicker
            key={`to-${clears}`}
            value={to}
            onChange={(v) => {
              setTo(v)
              if (v !== '') setToTyped(null)
            }}
            labelledBy={`${baseId}-to`}
            subject="end date"
            onTyped={(raw, valid) => setToTyped(valid || raw.length < 10 ? null : raw)}
            {...((toTyped !== null || rangeProblem !== null) && { invalid: true, describedBy: `${baseId}-to-error` })}
          />
          <FieldError
            id={`${baseId}-to-error`}
            problem={toTyped !== null ? `${toTyped} is not a real date. Use the form yyyy-mm-dd.` : rangeProblem}
          />
        </div>
        {anyFilter && (
          <div className="flex flex-col justify-end">
            <SgdsButton type="button" variant="outline" onClick={clearFilters}>
              Clear filters
            </SgdsButton>
          </div>
        )}
      </div>
      )}

      <SortAnnouncement sort={sort} interacted={interacted} busy={calculations.loading} columnLabels={CALC_COLUMN_LABELS} rowCount={rows.length} />

      {calculations.error !== null ? (
        <ErrorCard title="Could not load the calculations" message={calculations.error} {...(calculations.retryable && { onRetry: calculations.reload })} />
      ) : calculations.initialLoading ? (
        <LoadingCard label="Loading the calculations" rows={4} />
      ) : rangeProblem !== null ? (
        <EmptyCard title="Check the dates" message={rangeProblem} onClear={clearFilters} />
      ) : rows.length === 0 ? (
        <EmptyCard
          title="No calculation matches"
          message={
            anyFilter
              ? 'No recorded calculation matches these filters. Change a filter, or clear them to see every calculation.'
              : 'Nothing has been calculated yet. Run one in the calculator and it appears here.'
          }
          {...(anyFilter && { onClear: clearFilters })}
        />
      ) : (
        <div className="pt-card p-0 overflow-hidden">
          <div className="pt-table-wrap" aria-busy={calculations.loading}>
            <table className="pt-table">
              <caption className="sr-only">Recorded MSO calculations</caption>
              <thead>
                <tr>
                  <SortableTh label="Computed" sortKey="computed" sort={sort} onSort={onSort} />
                  <SortableTh label="Treatment date" sortKey="treatmentDate" sort={sort} onSort={onSort} />
                  <SortableTh label="Beneficiary" sortKey="beneficiary" sort={sort} onSort={onSort} />
                  <SortableTh label="Outcome" sortKey="outcome" sort={sort} onSort={onSort} />
                  <SortableTh label="Rule" sortKey="rule" sort={sort} onSort={onSort} />
                  <SortableTh label="Officer or dependant" sortKey="officer" sort={sort} onSort={onSort} align="right" />
                  <SortableTh label="Government" sortKey="government" sort={sort} onSort={onSort} align="right" />
                  <SortableTh label="Refusal" sortKey="refusal" sort={sort} onSort={onSort} />
                  <th scope="col" data-unsortable="Sorting by this column is not offered here.">
                    Calculated by <UnsortableHint />
                  </th>
                  <th scope="col" className="pt-no-print" data-unsortable="Sorting by this column is not offered here.">
                    Detail <UnsortableHint />
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="text-sm">{formatInstant(row.computedAt)}</td>
                    <td className="text-sm">{treatmentDateText(row)}</td>
                    <td className="pt-mono text-sm">{storedField(row, 'beneficiaryRef') ?? 'Not supplied'}</td>
                    <td>
                      <CategoryBadge kind="outcome" value={row.outcome} label={row.outcome === 'CALCULATED' ? 'Calculated' : 'Refused'} />
                    </td>
                    <td className="pt-mono whitespace-nowrap">{row.ruleKey === null ? 'Not selected' : `${row.ruleKey} v${row.ruleVersion}`}</td>
                    <td className="pt-num">
                      <Money cents={row.officerCents} placeholder="None" />
                    </td>
                    <td className="pt-num">
                      <Money cents={row.governmentCents} placeholder="None" />
                    </td>
                    <td className="text-sm">
                      {row.refusalKind === null ? 'None' : <CategoryBadge kind="refusal" value={row.refusalKind} label={msoRefusalLabel(row.refusalKind)} />}
                    </td>
                    <td className="text-sm">{row.computedByName ?? (row.computedBy === null ? 'Not recorded' : 'Name not available')}</td>
                    <td className="pt-no-print">
                      <Link to={`/mso/calculations/${row.id}`} aria-label={`Open calculation ${row.id}`}>
                        Open
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination pager={pager} label="calculations" total={total} shown={rows.length} />
          <p className="px-4 pb-4 text-sm text-[var(--sgds-body-color-subtle)]">
            {rows.length} of {total} calculation(s) shown.
          </p>
        </div>
      )}
    </div>
  )
}
